import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Wrapper, Header, Logo } from "../styles";
import PageWrapper from "../../../components/PageWrapper/PageWrapper";
import LanguageDropdown from "../../../components/LanguageDropdown";
import { JWT_TOKEN } from "../../../global/constants";
import {
  fetchSelf,
  switchTenant,
  rememberTenant,
  ssoRequiredUrl,
  TENANT_SELECT_SSO_URL,
} from "../../../services/tenant-services/tenantActions";
import { safeSsoUrl } from "../../../services/security-services/SecurityActions";
import "./TenantSelectionPage.css";

const initials = (name) =>
  (name || "?")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const targetRoute = () =>
  sessionStorage.getItem("current-page") || "/office/global";

const TenantSelectionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [username, setUsername] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  // SPEC-0076: a password session cannot enter an « SSO only » tenant.
  const [ssoUrl, setSsoUrl] = useState(() => {
    const handed = sessionStorage.getItem(TENANT_SELECT_SSO_URL);
    sessionStorage.removeItem(TENANT_SELECT_SSO_URL);
    return safeSsoUrl(handed);
  });

  useEffect(() => {
    if (!localStorage.getItem(JWT_TOKEN)) {
      navigate("/login");
      return;
    }
    fetchSelf()
      .then((self) => {
        const list = self.accessibleTenants || [];
        setTenants(list);
        setActiveId(self.tenant ? self.tenant.id : null);
        setUsername(self.username);
        // Defensive: a single-tenant user should never linger here.
        if (list.length <= 1) {
          navigate(targetRoute());
        }
      })
      .catch(() => navigate("/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((tn) => (tn.name || "").toLowerCase().includes(q));
  }, [tenants, query]);

  const choose = async (tenant) => {
    if (switching) return;
    // Same tenant: just continue into the app, remembering the choice.
    if (tenant.id === activeId) {
      rememberTenant(username, tenant.id);
      navigate(targetRoute());
      return;
    }
    setSwitching(true);
    setSsoUrl(null);
    try {
      await switchTenant(tenant.id);
      // Reload at the target so the app re-bootstraps under the new tenant.
      const route = targetRoute();
      sessionStorage.removeItem("current-page");
      window.location.assign(route);
    } catch (e) {
      setSsoUrl(ssoRequiredUrl(e));
      setSwitching(false);
    }
  };

  return (
    <PageWrapper>
      <LanguageDropdown />
      <Wrapper>
        <Header>
          <Logo src="/logo.png" alt="Offolio Logo" />
        </Header>
        <div className="tenant-select">
          <h2 className="tenant-select-title">{t("tenantSelect.title")}</h2>
          <p className="tenant-select-subtitle">{t("tenantSelect.subtitle")}</p>

          {ssoUrl && (
            <div className="alert alert-warning py-2 small" role="alert" id="tenant-select-error" data-testid="tenant-select-error">
              {t("loginPage.errors.ssoRequired")}{" "}
              <a href={ssoUrl} id="tenant-select-sso-link" data-testid="tenant-select-sso-link">
                {t("loginPage.signInWithSso")}
              </a>
            </div>
          )}

          {tenants.length > 6 && (
            <div className="tenant-select-search">
              <i className="fas fa-search" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("tenantSelect.searchPlaceholder")}
                data-testid="tenant-search"
                autoFocus
              />
            </div>
          )}

          {loading ? (
            <div className="tenant-select-loading">
              <span className="spinner-border spinner-border-sm" role="status" />
            </div>
          ) : (
            <div className="tenant-list" data-testid="tenant-list">
              {filtered.map((tenant) => (
                <button
                  key={tenant.id}
                  type="button"
                  className={`tenant-card${tenant.id === activeId ? " active" : ""}`}
                  onClick={() => choose(tenant)}
                  disabled={switching}
                  data-testid={`tenant-card-${tenant.id}`}
                >
                  <span className="tenant-avatar">{initials(tenant.name)}</span>
                  <span className="tenant-name">{tenant.name}</span>
                  {tenant.id === activeId && (
                    <span className="tenant-current">{t("tenantSelect.current")}</span>
                  )}
                  <i className="fas fa-chevron-right tenant-chevron" />
                </button>
              ))}
              {!filtered.length && (
                <div className="tenant-empty">{t("tenantSelect.noMatch")}</div>
              )}
            </div>
          )}
        </div>
      </Wrapper>
    </PageWrapper>
  );
};

export default TenantSelectionPage;
